// Specific modules rather than the public API, so the plain-logic test runner can load this file.
import {
  ReportingComparisonScopes,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import type {
  ReportFilterAgencyOptionDto,
  ReportFilterRmdOptionDto,
} from 'ag-common-lib/lib/contracts/reporting-api/response/report-filter-options';

/** One entry in the report pages' "Filter by RMD or MGA" box. */
export interface ReportFilterOption {
  value: string;
  label: string;
  /** The RMD's or MGA's name alone, where `label` may add the email to tell namesakes apart. */
  name?: string;
  searchText: string;
  type: 'RMD' | 'MGA';
}

/** The filter box's placeholder: an RMD's page lists only their MGAs; company-wide pages list both. */
export function getReportFilterPlaceholder(scope: ReportingComparisonScope | null): string {
  return scope === ReportingComparisonScopes.rmd ? 'Filter by MGA' : 'Filter by RMD or MGA';
}

export const sortReportFilterOptions = (options: ReportFilterOption[]): ReportFilterOption[] =>
  [...options].sort((left, right) =>
    left.label.localeCompare(right.label, 'en', {
      numeric: true,
      sensitivity: 'base',
      ignorePunctuation: true,
    }),
  );

export function mapRmdFilterOption(rmd: ReportFilterRmdOptionDto): ReportFilterOption | null {
  const agentId = String(rmd?.agentId ?? '').trim();
  if (!agentId) return null;

  const fullName = [rmd.firstName, rmd.lastName].filter(Boolean).join(' ') || String(rmd.fullName ?? '');
  const email = String(rmd.email ?? '').trim();

  return {
    value: `rmd:${agentId}`,
    label: email ? `${fullName} (${email})` : fullName,
    name: fullName,
    searchText: [agentId, fullName, email].filter(Boolean).join(' '),
    type: 'RMD',
  };
}

/**
 * An MGA option is keyed by the agency id, which the reports filter by: the paid report rows carry
 * the agency's name as the paid file spells it, which is not always the name typed here.
 */
export function mapMgaFilterOption(agency: ReportFilterAgencyOptionDto): ReportFilterOption | null {
  const agencyId = String(agency?.agencyId ?? '').trim();
  const agencyName = String(agency?.name ?? '').trim();
  if (!agencyId || !agencyName) return null;

  const contactEmail = String(agency.contactEmail ?? '').trim();

  return {
    value: `mga:${agencyId}`,
    label: agencyName,
    name: agencyName,
    searchText: [agencyName, agencyId, contactEmail].filter(Boolean).join(' '),
    type: 'MGA',
  };
}

/** The selected `rmd:` / `mga:` option values as the reporting API's mga[] / rmd[] filters. */
export function toReportFilters(values: readonly string[]): { mga: string[]; rmd: string[] } {
  return values.reduce(
    (filters, taggedValue) => {
      const separatorIndex = taggedValue.indexOf(':');
      if (separatorIndex < 0) return filters;

      const type = taggedValue.slice(0, separatorIndex);
      const value = taggedValue.slice(separatorIndex + 1).trim();
      if (!value) return filters;

      if (type === 'mga') filters.mga.push(value);
      if (type === 'rmd') filters.rmd.push(value);
      return filters;
    },
    { mga: [], rmd: [] } as { mga: string[]; rmd: string[] },
  );
}
